import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _toConsumableArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import React from "react";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { memo, useState, useEffect } from "react";
import Image from "next/image";
var SEGMENT_FILL_DELAY_MS = 120;
var DEFAULT_SLIDES_CONFIG = {
  total: 4,
  filledByPhase: {
    transverse: 0,
    longitudinal: 2,
    submit: 4
  }
};
var FILLED_SEGMENT_STYLE = {
  borderRadius: 24,
  background: "radial-gradient(136.22% 136.22% at 142.5% 13.75%, #833766 0%, #FF7869 100%)"
};
/**
 * Volume Sweep step view: header, illustration, instructions, slide progress.
 * Content is supplied by volumeSweepStepContent config; same shape can be used for 14-point and mediastinal.
 */
var ZoneIndicator = /*#__PURE__*/memo(function ZoneIndicator(_ref) {
  var _ref2, _ref3;
  var zoneLetter = _ref.zoneLetter,
    headerLeft = _ref.headerLeft,
    headerRight = _ref.headerRight,
    phase = _ref.phase,
    content = _ref.content,
    _ref$slidesConfig = _ref.slidesConfig,
    slidesConfig = _ref$slidesConfig === void 0 ? DEFAULT_SLIDES_CONFIG : _ref$slidesConfig;
  var slidesTotal = slidesConfig.total,
    filledByPhase = slidesConfig.filledByPhase;
  var filledSlides = filledByPhase[phase];
  var _useState = useState(filledSlides),
    animatedFilledCount = _useState[0],
    setAnimatedFilledCount = _useState[1];
  var _useState2 = useState(function () {
      return new Set(Array.from({
        length: filledSlides
      }, function (_, i) {
        return i;
      }));
    }),
    expandedSegments = _useState2[0],
    setExpandedSegments = _useState2[1];

  // Animate segments filling one-by-one when filledSlides increases
  useEffect(function () {
    if (animatedFilledCount < filledSlides) {
      var timeout = setTimeout(function () {
        return setAnimatedFilledCount(function (prev) {
          return Math.min(prev + 1, filledSlides);
        });
      }, SEGMENT_FILL_DELAY_MS);
      return function () {
        return clearTimeout(timeout);
      };
    }
    if (animatedFilledCount > filledSlides) {
      setAnimatedFilledCount(filledSlides);
      setExpandedSegments(new Set(Array.from({
        length: filledSlides
      }, function (_, i) {
        return i;
      })));
    }
  }, [filledSlides, animatedFilledCount]);

  // Trigger expand animation for the segment that just became filled (paint scaleX(0) first, then expand)
  useEffect(function () {
    if (animatedFilledCount === 0) return;
    var newIndex = animatedFilledCount - 1;
    if (expandedSegments.has(newIndex)) return;
    var frame = requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        setExpandedSegments(function (prev) {
          return new Set([].concat(_toConsumableArray(prev), [newIndex]));
        });
      });
    });
    return function () {
      return cancelAnimationFrame(frame);
    };
  }, [animatedFilledCount, expandedSegments]);
  var leftLabel = (_ref2 = headerLeft !== null && headerLeft !== void 0 ? headerLeft : zoneLetter) !== null && _ref2 !== void 0 ? _ref2 : "";
  var rightLabel = (_ref3 = headerRight !== null && headerRight !== void 0 ? headerRight : content.label) !== null && _ref3 !== void 0 ? _ref3 : "";
  return /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-full flex items-stretch gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex-1 rounded-xl bg-gray-800/60 px-5 py-4 flex justify-between items-center"
  }, /*#__PURE__*/React.createElement("p", {
    className: "text-white text-2xl font-bold leading-tight"
  }, leftLabel), /*#__PURE__*/React.createElement("p", {
    className: "text-white text-lg font-semibold"
  }, function () {
    var words = rightLabel.trim().split(/\s+/);
    if (words.length === 2) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, words[0], /*#__PURE__*/React.createElement("br", null), words[1]);
    }
    return rightLabel;
  }())), /*#__PURE__*/React.createElement("div", {
    className: "rounded-xl overflow-hidden flex items-center justify-center w-[95px] bg-gray-800/60"
  }, phase === "transverse" ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-stretch w-full h-full py-2 pl-2 pr-0 pb-0"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-[3px] h-[45px] rounded-full bg-brand-peach self-center ml-5"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-1 items-end justify-end min-w-0 w-fit"
  }, /*#__PURE__*/React.createElement(Image, {
    src: "/assets/images/scan_assist/probe_tilt.png",
    alt: "",
    width: 72,
    height: 72,
    className: "flex-shrink-0 object-contain object-right-bottom"
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col items-center justify-center w-full py-3 px-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-8 h-[3px] rounded-full bg-brand-peach mb-2 shrink-0"
  }), /*#__PURE__*/React.createElement(Image, {
    src: "/assets/images/scan_assist/probe_vertical.png",
    alt: "",
    width: 32,
    height: 32,
    className: "object-contain"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "relative w-full aspect-[4/3] mt-4 flex-shrink-0"
  }, /*#__PURE__*/React.createElement(Image, {
    src: content.imageSrc,
    alt: "",
    fill: true,
    className: "object-contain",
    sizes: "(max-width: 100%) 280px, 320px"
  })), /*#__PURE__*/React.createElement("p", {
    className: "text-gray-200 font-medium mt-6 text-center"
  }, content.instructionLine1), /*#__PURE__*/React.createElement("p", {
    className: "text-gray-200 mt-1 text-center"
  }, content.instructionLine2), content.secondaryInstruction && /*#__PURE__*/React.createElement("p", {
    className: "text-teal-400 text-base font-medium mt-3 text-center"
  }, content.secondaryInstruction), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center gap-1.5 mt-6",
    role: "progressbar",
    "aria-valuenow": filledSlides,
    "aria-valuemin": 0,
    "aria-valuemax": slidesTotal
  }, Array.from({
    length: slidesTotal
  }, function (_, i) {
    var isFilled = i < animatedFilledCount;
    var isExpanded = expandedSegments.has(i);
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "h-3 flex-1 min-w-[45px] max-w-[48px] rounded-full overflow-hidden ".concat(!isFilled ? "bg-gray-800" : "")
    }, isFilled && /*#__PURE__*/React.createElement("div", {
      className: "h-full w-full rounded-full origin-left transition-transform duration-300 ease-out",
      style: _objectSpread(_objectSpread({}, FILLED_SEGMENT_STYLE), {}, {
        transform: isExpanded ? "scaleX(1)" : "scaleX(0)"
      })
    }));
  })));
});
export default ZoneIndicator;